import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Radar, Plus, Trash2, Send, Eye, Clock, Mail, Power, Pencil, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQ = { daily: 'Diário', weekly: 'Semanal (segunda a domingo)', monthly: 'Mensal (mês fechado)' };

const BLOCKS = [
  { key: 'narrative', label: 'Leitura do período (IA)' },
  { key: 'focus', label: 'Onde atuar (4 cartões)' },
  { key: 'web', label: 'Saúde web (Web Monitor)' },
  { key: 'recommendations', label: 'Direcionamento para atuação (IA)' },
];

const emptyForm = {
  name: 'Radar Operacional', frequency: 'weekly', org_id: '', group_ids: [],
  recipients: '', send_hour_utc: 10, include_ai_analysis: true, attach_pdf: true,
  blocks: { narrative: true, focus: true, web: true, recommendations: true },
};

export function RadarPage() {
  const [configs, setConfigs] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const { getAuthHeader } = useAuthStore();
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const fetchData = useCallback(async () => {
    try {
      const [res, orgsRes] = await Promise.all([
        axios.get(`${API}/radar/configs`, { headers: authRef.current() }),
        axios.get(`${API}/orgs`, { headers: authRef.current() }).catch(() => ({ data: [] })),
      ]);
      setConfigs(res.data.configs || []);
      setOrgs(orgsRes.data);
    } catch (_) {
      toast.error('Erro ao carregar configurações do Radar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const payload = () => ({
    name: form.name, frequency: form.frequency, org_id: form.org_id || null,
    group_ids: form.group_ids, recipients: form.recipients.split(',').map(e => e.trim()).filter(Boolean),
    send_hour_utc: Number(form.send_hour_utc), include_ai_analysis: form.include_ai_analysis,
    attach_pdf: form.attach_pdf, blocks: form.blocks, enabled: true,
  });

  const save = async () => {
    if (!form.name || !form.recipients) { toast.error('Informe nome e destinatários'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/radar/configs/${editing.id}`, payload(), { headers: authRef.current() });
        toast.success('Radar atualizado');
      } else {
        await axios.post(`${API}/radar/configs`, payload(), { headers: authRef.current() });
        toast.success('Radar configurado');
      }
      setDialogOpen(false); setEditing(null); setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const openEdit = (cfg) => {
    setEditing(cfg);
    setForm({
      name: cfg.name, frequency: cfg.frequency || 'weekly', org_id: cfg.org_id || '',
      group_ids: cfg.group_ids || [], recipients: (cfg.recipients || []).join(', '),
      send_hour_utc: cfg.send_hour_utc ?? 10, include_ai_analysis: cfg.include_ai_analysis !== false,
      attach_pdf: cfg.attach_pdf !== false,
      blocks: { narrative: true, focus: true, web: true, recommendations: true, ...(cfg.blocks || {}) },
    });
    setDialogOpen(true);
  };

  const toggleEnabled = async (cfg) => {
    await axios.put(`${API}/radar/configs/${cfg.id}`, { enabled: !cfg.enabled }, { headers: authRef.current() });
    setConfigs(prev => prev.map(c => c.id === cfg.id ? { ...c, enabled: !c.enabled } : c));
  };

  const remove = async (cfg) => {
    if (!window.confirm(`Excluir a configuração ${cfg.name}?`)) return;
    await axios.delete(`${API}/radar/configs/${cfg.id}`, { headers: authRef.current() });
    toast.success('Configuração excluída');
    fetchData();
  };

  const sendNow = async (cfg) => {
    toast.info('Gerando e enviando o Radar...');
    try {
      const res = await axios.post(`${API}/radar/configs/${cfg.id}/send-now`, {}, { headers: authRef.current() });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar');
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/radar/preview`, payload(), {
        headers: authRef.current(), responseType: 'text',
      });
      setPreviewHtml(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar pré-visualização');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-60 w-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="radar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Radar className="h-5 w-5" /> Radar Operacional de Tecnologia
          </h2>
          <p className="text-sm text-muted-foreground">
            Resumo executivo por e-mail — diário, semanal e mensal, com análise de IA e PDF anexo
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setDialogOpen(true); }}
          data-testid="new-radar-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo Radar
        </Button>
      </div>

      {configs.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <Radar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-medium">Nenhum Radar configurado</h3>
          <p className="text-sm text-muted-foreground">
            Crie um Radar semanal por cliente e valide com &quot;Pré-visualizar&quot; antes de ativar
          </p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {configs.map(cfg => (
            <Card key={cfg.id} className={!cfg.enabled ? 'opacity-60' : ''} data-testid={`radar-card-${cfg.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base">{cfg.name}</CardTitle>
                  <div className="flex items-center gap-1">
                    <Switch checked={cfg.enabled} onCheckedChange={() => toggleEnabled(cfg)} className="scale-75" />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => sendNow(cfg)} title="Enviar agora"
                      data-testid={`send-radar-${cfg.id}`}><Send className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(cfg)}
                      data-testid={`edit-radar-${cfg.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(cfg)}
                      data-testid={`delete-radar-${cfg.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />{FREQ[cfg.frequency]?.split(' ')[0]} · {String(cfg.send_hour_utc).padStart(2, '0')}:00 UTC</Badge>
                  <Badge variant={cfg.enabled ? 'default' : 'outline'}><Power className="h-3 w-3 mr-1" />{cfg.enabled ? 'Ativo' : 'Inativo'}</Badge>
                  {cfg.include_ai_analysis !== false && <Badge variant="outline"><Sparkles className="h-3 w-3 mr-1" />IA</Badge>}
                </div>
                <div className="text-muted-foreground">
                  Cliente: {orgs.find(o => o.id === cfg.org_id)?.display_name || 'Todos os grupos do tenant'}
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="truncate">{(cfg.recipients || []).join(', ')}</span>
                </div>
                {cfg.last_sent && (
                  <p className="text-xs text-muted-foreground">
                    Último envio: {new Date(cfg.last_sent).toLocaleString('pt-BR')}
                    {cfg.last_status === 'error' && <span className="text-destructive ml-1">(falha)</span>}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Config dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[88vh]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Radar' : 'Novo Radar'}</DialogTitle>
            <DialogDescription>
              Comparação automática com o período anterior equivalente e selo ESTÁVEL/ATENÇÃO/CRÍTICO
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="radar-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Periodicidade</Label>
              <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v }))}>
                <SelectTrigger data-testid="radar-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cliente/Org</Label>
              <Select value={form.org_id} onValueChange={v => setForm(f => ({ ...f, org_id: v }))}>
                <SelectTrigger data-testid="radar-org-select"><SelectValue placeholder="Todos os grupos do tenant" /></SelectTrigger>
                <SelectContent>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Destinatários (separados por vírgula) *</Label>
              <Input value={form.recipients} onChange={e => setForm(f => ({ ...f, recipients: e.target.value }))}
                placeholder="diretoria@empresa.com, noc@empresa.com" data-testid="radar-recipients-input" />
            </div>
            <div className="space-y-2">
              <Label>Horário de envio (UTC)</Label>
              <Select value={String(form.send_hour_utc)}
                onValueChange={v => setForm(f => ({ ...f, send_hour_utc: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00 UTC</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Blocos incluídos</Label>
              {BLOCKS.map(b => (
                <div key={b.key} className="flex items-center gap-2">
                  <Checkbox id={`b-${b.key}`} checked={form.blocks[b.key] !== false}
                    onCheckedChange={() => setForm(f => ({
                      ...f, blocks: { ...f.blocks, [b.key]: f.blocks[b.key] === false },
                    }))} data-testid={`block-${b.key}`} />
                  <label htmlFor={`b-${b.key}`} className="text-sm cursor-pointer">{b.label}</label>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <Label>Análise por IA</Label>
              <Switch checked={form.include_ai_analysis}
                onCheckedChange={v => setForm(f => ({ ...f, include_ai_analysis: v }))} data-testid="radar-ai-switch" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Anexar PDF equivalente</Label>
              <Switch checked={form.attach_pdf}
                onCheckedChange={v => setForm(f => ({ ...f, attach_pdf: v }))} data-testid="radar-pdf-switch" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={preview} disabled={previewing} data-testid="preview-radar-btn">
              <Eye className="h-4 w-4 mr-2" /> {previewing ? 'Gerando...' : 'Pré-visualizar'}
            </Button>
            <Button onClick={save} data-testid="save-radar-btn">{editing ? 'Salvar' : 'Criar Radar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!previewHtml} onOpenChange={() => setPreviewHtml(null)}>
        <DialogContent className="sm:max-w-4xl h-[88vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Pré-visualização do Radar</DialogTitle>
            <DialogDescription>Mesma renderização enviada por e-mail (640px)</DialogDescription>
          </DialogHeader>
          <iframe title="Radar preview" srcDoc={previewHtml} className="flex-1 w-full border rounded"
            data-testid="radar-preview-frame" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
